/**
 * LocaleReconciler — runs once on mount in the client.
 *
 * SSR cannot read localStorage, so the server may pick a locale that
 * disagrees with the user's persisted choice. This component reconciles:
 *
 *   - If localStorage[hantavirus_locale] is set, valid, and ≠ pageContext.locale:
 *       1. Write the cookie so future SSR converges.
 *       2. Hard-replace navigation to the localized URL (so the prerendered
 *          locale bundle + `<html lang>` match the user's choice).
 *
 *   - If localStorage is empty: seed it with the server's pick.
 *
 * The reconcile runs in `useLayoutEffect` to fire before paint when possible.
 * During the brief window between mount and the navigation kicking in, we
 * render the page as-is (no visual skeleton needed — the navigation is
 * synchronous from the user's perspective: < 1 frame).
 */

import { useEffect, useLayoutEffect } from "react";

import {
  LOCALE_COOKIE,
  LOCALE_STORAGE_KEY,
  isLocaleCode,
  type LocaleCode,
} from "#/lib/i18n/locales.js";
import { getLocalizedHref } from "#/components/layout/PublicChrome.js";

const COOKIE_MAX_AGE_SECONDS = 31536000; // 1 year

export interface LocaleReconcilerProps {
  /** Locale chosen by SSR (URL > cookie > Accept-Language > default). */
  serverLocale: LocaleCode;
  /** Pathname with the locale prefix already stripped (e.g. "/outbreak/foo"). */
  currentLogicalPath: string;
}

// Pick `useLayoutEffect` on the client, no-op on server. Avoids the React
// SSR warning about useLayoutEffect in node.
const useIsoLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

export function LocaleReconciler({
  serverLocale,
  currentLogicalPath,
}: LocaleReconcilerProps): null {
  useIsoLayoutEffect(() => {
    if (typeof window === "undefined") return;

    let stored: string | null = null;
    try {
      stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    } catch {
      // localStorage unavailable; nothing to reconcile.
      return;
    }

    if (stored && isLocaleCode(stored)) {
      if (stored !== serverLocale) {
        // Persisted choice disagrees with what SSR picked. Write the cookie
        // so future SSR converges, then redirect to the user's locale.
        document.cookie = `${LOCALE_COOKIE}=${encodeURIComponent(stored)}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
        const dest = getLocalizedHref(stored, currentLogicalPath);
        if (dest !== window.location.pathname) {
          window.location.replace(dest);
        }
      }
      return;
    }

    // No persisted choice — seed localStorage with the server pick so future
    // private-mode visits or other-tab visits stay consistent.
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, serverLocale);
    } catch {
      // ignore — best effort.
    }
  }, [serverLocale, currentLogicalPath]);

  return null;
}
